/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  PropertyDefinitions,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, DropTarget } from './DropTarget';
import { CommonProps } from '../../layout/Sidebar/panels/PropertiesView/CommonProperties';

export const DropTargetProps: PropertyDefinitions<
  Omit<DropTarget, keyof CommonProps>
> = {
  type: {
    label: 'Type',
    type: 'string',
    helpText:
      'You can specify a type value for this drop target. Only the Drag Sources with the same type can be dropped on this.',
    required: true,
  },
  canDropCondition: {
    label: 'Can Drop Condition',
    type: 'expressionOnly',
    returnType: () => 'boolean',
  },
  canDrop: {
    label: 'canDrop Function',
    type: 'controllerFunction',
    category: 'Controllers',
  },
  onDrop: {
    label: 'onDrop Function',
    type: 'controllerFunction',
    category: 'Controllers',
  },
};

const DropTargetManager: ComponentManagerFn<'DropTarget'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'DropTarget'>(platform, 'DropTarget'),
    type: 'DropTarget',
    name: 'Drop Target',
    isChildAllowed: (page, item, childType) =>
      (['Card', 'RowPanel', 'TriggerOnDrop'] as ItemType[]).includes(childType),
    defaultValue,
    props: DropTargetProps,
    canHaveChildren: () => true,
    // getDisplayText: (item) => item.props.icon?.iconName ?? 'Choose icon!',
    category: 'Containers',
    icon: { iconName: 'expand-wide' },
    description: `**Overview**<br/>
The Drop Target is a functional wrapper component that defines a designated "landing zone" for drag-and-drop interactions. While the Drag Source allows an item to be picked up, the Drop Target determines where that item can be placed.
By wrapping a section of your layout (like a column in a Kanban board or a folder in a file manager) with a Drop Target, you enable it to receive data and visual elements from other parts of the application.

**1. Defining Compatibility (The Handshake)**<br/>
To prevent logical errors—like dropping a "Customer Record" into a "Date Picker"—the system relies on a strict matching protocol.
* **Type:** A mandatory string identifier (e.g., 'task-card', 'inventory-item'). This acts as the receiving lock. This Drop Target will only accept items from a Drag Source that broadcasts this exact same Type. If the types do not match, the drop zone will simply ignore the dragged item.

**2. Controlling Interaction Rules**<br/>
You often need to enforce specific business rules before allowing a drop (e.g., "You cannot drop a Task into a Completed column if it has open sub-tasks").
* **Can Drop Condition:** A boolean expression (True/False). Use this for high-level state checks, such as disabling a drop zone entirely based on user permissions or application mode.
* **canDrop Function:** A controller function for granular validation. This logic runs in real-time while the user is dragging an item over the target. It allows you to inspect the incoming data and return false to reject the drop dynamically (for example, preventing a duplicate item from being added to a list).

**3. Event Handling (The Action)**<br/>
Once a valid item is dropped, you need to tell the application what to do next.
* **onDrop Function:** This is the critical trigger that executes immediately after a successful drop. You typically use this function to update the database, move the UI element from the old list to the new list, or trigger a notification.

**4. Structure & Content**<br/>
Like the Drag Source, the Drop Target is a logical wrapper that requires child content to define its visual area.
* **Supported Children:** You can place visual components inside the Drop Target to indicate the drop area to the user.
* **Card & Row Panel:** Common visual containers.
* **TriggerOnDrop:** A specialized child component used to execute specific workflows upon a drop event.`,
  };
};

export default DropTargetManager;
